/**
 * Path helpers shared by the blog listing templates and `gatsby-node.js`.
 *
 * NOTE: this module is CommonJS on purpose - unlike the rest of `src/utils`.
 * `gatsby-node.js` requires it directly and is not run through babel, so it
 * cannot consume an ES module. Webpack/babel/jest give the React side normal
 * interop, so components can still `import` from it.
 */

/**
 * Categories that already own a canonical listing URL elsewhere, so we don't
 * publish two listings of the same articles.
 *
 * This is the single source of truth for the override: `gatsby-node.js` builds
 * the listing at this path, and the article tag chips link to it. Keeping the
 * two apart is what made every `thinkmoney News` chip point at a
 * `/blog/category/thinkmoney-news/` page that is never built.
 */
const CATEGORY_ROUTE_OVERRIDES = {
    'thinkmoney-news': { basePath: '/blog/news/', variant: 'news' }
};

/**
 * Derive a URL slug from a DatoCMS blog category name.
 *
 * The `blog_category` model has no slug field, so category routes are keyed
 * off the name. `&` is expanded before punctuation is collapsed so that
 * "Credit & Loans" and the legacy `?category=credit-%26-loans` query param
 * both resolve to the same slug.
 */
const slugifyCategory = (name) =>
    String(name || '')
        .toLowerCase()
        .replace(/&/g, ' and ')
        .replace(/['’]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

/**
 * The listing route that owns a category slug.
 */
const categoryBasePath = (slug) =>
    CATEGORY_ROUTE_OVERRIDES[slug]?.basePath || `/blog/category/${slug}/`;

/**
 * Which listing template variant a category slug renders as. An overridden
 * category has its own URL and so its own CMS-managed hero and SEO tags.
 */
const categoryVariant = (slug) =>
    CATEGORY_ROUTE_OVERRIDES[slug]?.variant || 'category';

/**
 * The listing route for a category *name*, for linking straight off an
 * article's categories. A name that slugifies to nothing has no listing of its
 * own, so it falls back to the unfiltered blog.
 */
const categoryPath = (name) => {
    const slug = slugifyCategory(name);

    return slug ? categoryBasePath(slug) : '/blog/';
};

/**
 * Build the path for a page of a paginated listing.
 *
 * Page 1 keeps the bare base path so existing URLs are unchanged; subsequent
 * pages are nested under it (`/blog/` -> `/blog/2/`).
 */
const listingPath = (basePath, page) =>
    page > 1 ? `${basePath}${page}/` : basePath;

/**
 * Strip the leading and trailing slashes from a path, for `<Canonical />`
 * which builds `https://www.thinkmoney.co.uk/${href}/`.
 */
const toCanonicalHref = (path) => path.replace(/^\/+|\/+$/g, '');

module.exports = {
    CATEGORY_ROUTE_OVERRIDES,
    slugifyCategory,
    categoryBasePath,
    categoryVariant,
    categoryPath,
    listingPath,
    toCanonicalHref
};
